import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Mail, X } from "lucide-react";

const DEFAULT_MSG = `I've been using Talkademia — an academic discussion platform where I host lectures, publish papers for open discussion, and engage with other scholars. I thought you'd find it useful. Join me there.`;

// Browser-safe row-ID generator. crypto.randomUUID() is unavailable in some
// corporate / non-secure contexts and would crash React's useState initializer
// if used directly. This monotonic counter is plenty unique for a single
// dialog with at most 10 rows.
let _rowCounter = 0;
const newRowId = () => {
  _rowCounter += 1;
  return `row-${Date.now()}-${_rowCounter}`;
};

export default function InviteColleagueDialog({ open, onOpenChange, trigger }) {
  const { user } = useAuth();
  const [emails, setEmails] = useState([{ id: newRowId(), value: "" }]);
  const [message, setMessage] = useState(DEFAULT_MSG);
  const [busy, setBusy] = useState(false);

  const updateEmail = (id, v) => setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, value: v } : e)));
  const addRow = () => { if (emails.length < 10) setEmails((prev) => [...prev, { id: newRowId(), value: "" }]); };
  const removeRow = (id) => setEmails((prev) => (prev.length === 1 ? [{ id: newRowId(), value: "" }] : prev.filter((e) => e.id !== id)));

  const reset = () => { setEmails([{ id: newRowId(), value: "" }]); setMessage(DEFAULT_MSG); };

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = emails.map((x) => x.value.trim()).filter((x) => x && x.includes("@"));
    if (cleaned.length === 0) { toast.error("Add at least one valid email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/invites", { emails: cleaned, message: message.trim() });
      const sent = data?.sent_count ?? 0;
      const attempted = data?.attempted ?? cleaned.length;
      if (sent === 0) {
        toast.warning(
          "We couldn't send your invitations right now (email service is rate-limited). Please try again in a few hours.",
          { duration: 9000 },
        );
      } else if (sent < attempted) {
        toast.warning(
          `Sent ${sent} of ${attempted} invitations — please retry the rest in a few minutes.`,
        );
      } else {
        toast.success(`Invite sent to ${sent} colleague${sent === 1 ? "" : "s"}.`);
      }
      reset();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger}
      <DialogContent className="sm:max-w-lg" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-navy">Invite a colleague</DialogTitle>
          <DialogDescription>
            Send a personal note to scholars in your circle. They'll get an email from Talkademia with your message.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email addresses</label>
            {emails.map((em, i) => (
              <div key={em.id} className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <Input
                    type="email"
                    value={em.value}
                    onChange={(e) => updateEmail(em.id, e.target.value)}
                    placeholder="colleague@university.edu"
                    className="pl-9"
                    data-testid={`invite-email-${i}`}
                  />
                </div>
                {emails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(em.id)}
                    className="p-2 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                    data-testid={`invite-remove-${i}`}
                    aria-label="Remove email"
                  >
                    <X className="h-4 w-4"/>
                  </button>
                )}
              </div>
            ))}
            {emails.length < 10 && (
              <button
                type="button"
                onClick={addRow}
                className="text-xs font-medium text-teal hover:text-teal-700 hover:underline"
                data-testid="invite-add-row"
              >+ Add another colleague</button>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Personal message</label>
            <Textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write a short note about why they should join you here…"
              maxLength={2000}
              className="border-slate-200 focus-visible:border-teal resize-none"
              data-testid="invite-message"
            />
            <div className="text-[11px] text-slate-400 flex justify-between">
              <span>The message appears below your name and handle in the email.</span>
              <span>{message.length}/2000</span>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid="invite-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-teal hover:bg-teal-700 text-white" data-testid="invite-send">
              {busy ? "Sending…" : <><Send className="h-4 w-4 mr-1.5"/>Send invite{emails.filter((e) => e.value && e.value.trim()).length > 1 ? "s" : ""}</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
